import { useSeoMeta } from '@unhead/react';
import { useState, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';

type Operation = '+' | '-' | '×' | '÷' | null;

interface CalcState {
  display: string;
  prev: number | null;
  operation: Operation;
  waitingForOperand: boolean;
  expression: string;
}

const initialState: CalcState = {
  display: '0',
  prev: null,
  operation: null,
  waitingForOperand: false,
  expression: '',
};

function calculate(a: number, b: number, op: Operation): number {
  switch (op) {
    case '+': return a + b;
    case '-': return a - b;
    case '×': return a * b;
    case '÷': return b !== 0 ? a / b : NaN;
    default: return b;
  }
}

function formatDisplay(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return 'Error';
  if (!isFinite(num)) return 'Error';
  if (value.endsWith('.')) return value;
  if (value.includes('.') && value.split('.')[1] !== undefined) return value;
  if (Math.abs(num) >= 1e15) return num.toExponential(6);
  return value;
}

const buttonVariants = {
  number: 'bg-white/10 hover:bg-white/20 active:bg-white/30 text-white',
  operator: 'bg-amber-400/90 hover:bg-amber-300 active:bg-amber-200 text-gray-900',
  function: 'bg-white/20 hover:bg-white/30 active:bg-white/40 text-white',
  equals: 'bg-amber-400/90 hover:bg-amber-300 active:bg-amber-200 text-gray-900',
  zero: 'bg-white/10 hover:bg-white/20 active:bg-white/30 text-white col-span-2 justify-start pl-7',
};

interface ButtonProps {
  label: string;
  variant: keyof typeof buttonVariants;
  onClick: () => void;
  className?: string;
}

function CalcButton({ label, variant, onClick, className }: ButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center justify-center rounded-full text-2xl font-medium',
        'h-16 w-16 transition-all duration-100 select-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400',
        'shadow-md active:scale-95',
        buttonVariants[variant],
        className
      )}
    >
      {label}
    </button>
  );
}

const Index = () => {
  useSeoMeta({
    title: 'Calcly — Simple Calculator',
    description: 'A clean, elegant calculator app.',
  });

  const [state, setState] = useState<CalcState>(initialState);

  const inputDigit = useCallback((digit: string) => {
    setState((prev) => {
      if (prev.waitingForOperand) {
        return {
          ...prev,
          display: digit,
          waitingForOperand: false,
          expression: prev.expression,
        };
      }
      const newDisplay =
        prev.display === '0' && digit !== '.'
          ? digit
          : prev.display.length < 12
          ? prev.display.includes('.') && digit === '.'
            ? prev.display
            : prev.display + digit
          : prev.display;
      return { ...prev, display: newDisplay };
    });
  }, []);

  const inputDecimal = useCallback(() => {
    setState((prev) => {
      if (prev.waitingForOperand) {
        return { ...prev, display: '0.', waitingForOperand: false };
      }
      if (prev.display.includes('.')) return prev;
      return { ...prev, display: prev.display + '.' };
    });
  }, []);

  const handleOperator = useCallback((op: Operation) => {
    setState((prev) => {
      const current = parseFloat(prev.display);
      if (prev.operation && !prev.waitingForOperand) {
        const result = calculate(prev.prev!, current, prev.operation);
        const resultStr = isNaN(result) || !isFinite(result) ? 'Error' : String(parseFloat(result.toPrecision(12)));
        return {
          display: resultStr,
          prev: isNaN(result) || !isFinite(result) ? null : result,
          operation: op,
          waitingForOperand: true,
          expression: `${resultStr} ${op ?? ''}`,
        };
      }
      return {
        ...prev,
        prev: current,
        operation: op,
        waitingForOperand: true,
        expression: `${prev.display} ${op ?? ''}`,
      };
    });
  }, []);

  const handleEquals = useCallback(() => {
    setState((prev) => {
      if (!prev.operation || prev.prev === null) return prev;
      const current = parseFloat(prev.display);
      const result = calculate(prev.prev, current, prev.operation);
      const resultStr = isNaN(result) || !isFinite(result) ? 'Error' : String(parseFloat(result.toPrecision(12)));
      return {
        display: resultStr,
        prev: null,
        operation: null,
        waitingForOperand: true,
        expression: `${prev.expression} ${prev.display} =`,
      };
    });
  }, []);

  const handleClear = useCallback(() => {
    setState(initialState);
  }, []);

  const handleToggleSign = useCallback(() => {
    setState((prev) => {
      if (prev.display === '0' || prev.display === 'Error') return prev;
      const toggled = prev.display.startsWith('-')
        ? prev.display.slice(1)
        : '-' + prev.display;
      return { ...prev, display: toggled };
    });
  }, []);

  const handlePercent = useCallback(() => {
    setState((prev) => {
      const num = parseFloat(prev.display);
      if (isNaN(num)) return prev;
      const result = num / 100;
      return { ...prev, display: String(parseFloat(result.toPrecision(12))) };
    });
  }, []);

  const handleBackspace = useCallback(() => {
    setState((prev) => {
      if (prev.waitingForOperand || prev.display === 'Error') return { ...prev, display: '0' };
      if (prev.display.length === 1 || (prev.display.length === 2 && prev.display.startsWith('-'))) {
        return { ...prev, display: '0' };
      }
      return { ...prev, display: prev.display.slice(0, -1) };
    });
  }, []);

  // Keyboard support
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') inputDigit(e.key);
      else if (e.key === '.') inputDecimal();
      else if (e.key === '+') handleOperator('+');
      else if (e.key === '-') handleOperator('-');
      else if (e.key === '*') handleOperator('×');
      else if (e.key === '/') { e.preventDefault(); handleOperator('÷'); }
      else if (e.key === 'Enter' || e.key === '=') handleEquals();
      else if (e.key === 'Backspace') handleBackspace();
      else if (e.key === 'Escape') handleClear();
      else if (e.key === '%') handlePercent();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [inputDigit, inputDecimal, handleOperator, handleEquals, handleBackspace, handleClear, handlePercent]);

  const displayValue = formatDisplay(state.display);
  const displayLength = displayValue.length;
  const fontSize =
    displayLength > 12 ? 'text-3xl' :
    displayLength > 9 ? 'text-4xl' :
    displayLength > 6 ? 'text-5xl' :
    'text-6xl';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-slate-900 to-gray-950 p-4">
      {/* Calculator card */}
      <div className="w-full max-w-xs bg-black/60 backdrop-blur-xl rounded-3xl overflow-hidden shadow-2xl border border-white/10">

        {/* Display */}
        <div className="px-6 pt-8 pb-4 flex flex-col items-end gap-1 min-h-[140px] justify-end">
          <p className="text-white/40 text-sm h-5 truncate max-w-full text-right">
            {state.expression || ' '}
          </p>
          <p
            className={cn(
              'text-white font-light tracking-tight transition-all duration-150 leading-none',
              fontSize
            )}
          >
            {displayValue}
          </p>
        </div>

        {/* Buttons */}
        <div className="grid grid-cols-4 gap-3 p-5">
          {/* Row 1 */}
          <CalcButton label={state.display !== '0' || state.operation ? 'C' : 'AC'} variant="function" onClick={handleClear} />
          <CalcButton label="+/-" variant="function" onClick={handleToggleSign} />
          <CalcButton label="%" variant="function" onClick={handlePercent} />
          <CalcButton label="÷" variant="operator" onClick={() => handleOperator('÷')} />

          {/* Row 2 */}
          <CalcButton label="7" variant="number" onClick={() => inputDigit('7')} />
          <CalcButton label="8" variant="number" onClick={() => inputDigit('8')} />
          <CalcButton label="9" variant="number" onClick={() => inputDigit('9')} />
          <CalcButton label="×" variant="operator" onClick={() => handleOperator('×')} />

          {/* Row 3 */}
          <CalcButton label="4" variant="number" onClick={() => inputDigit('4')} />
          <CalcButton label="5" variant="number" onClick={() => inputDigit('5')} />
          <CalcButton label="6" variant="number" onClick={() => inputDigit('6')} />
          <CalcButton label="−" variant="operator" onClick={() => handleOperator('-')} />

          {/* Row 4 */}
          <CalcButton label="1" variant="number" onClick={() => inputDigit('1')} />
          <CalcButton label="2" variant="number" onClick={() => inputDigit('2')} />
          <CalcButton label="3" variant="number" onClick={() => inputDigit('3')} />
          <CalcButton label="+" variant="operator" onClick={() => handleOperator('+')} />

          {/* Row 5 */}
          <CalcButton label="0" variant="zero" onClick={() => inputDigit('0')} className="col-span-2 w-full rounded-full" />
          <CalcButton label="." variant="number" onClick={inputDecimal} />
          <CalcButton label="=" variant="equals" onClick={handleEquals} />
        </div>
      </div>

      {/* Footer */}
      <p className="mt-6 text-white/30 text-xs">
        Vibed with{' '}
        <a
          href="https://marlowe.shakespeare.wtf"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-white/50 transition-colors"
        >
          Marlowe
        </a>
      </p>
    </div>
  );
};

export default Index;
